import React from 'react';

import { useTheme } from '@mui/material';
import type { VariantType } from 'notistack';
import { useSnackbar } from 'notistack';
import { BaseSnackbar, LinkSnackbar, SelectButton } from 'src/components';
import { pushNotification } from 'src/notifications';
import { v4 as uuid4 } from 'uuid';

export const fakeNotistackMessage = 'Some message that will not be rendered by Notistack.';
export const defaultSnackBarDisplayTimeMS = 5000;

const defaultErrorMessage = 'Something went wrong, please try again';
const defaultSuccessMessage = 'Success!';

interface SnackOptions {
  autoHideDurationMS?: number;
  key?: string;
}

interface MsgOptionalSnack extends SnackOptions {
  message?: string;
}

interface MsgRequiredSnack extends SnackOptions {
  message: string;
}

interface DispatchCustomSnackProps {
  autoHideDuration?: number;
  'data-cy'?: string;
  persist?: boolean;
  snackId?: UUID;
}

const TEN_SECONDS_MS = 10000;

export function useDispatchCustomSnackFn(
  snackProps: DispatchCustomSnackProps = {}
): (content: JSX.Element) => void {
  const { enqueueSnackbar } = useSnackbar();
  const { autoHideDuration = TEN_SECONDS_MS, persist, snackId = uuid4() } = snackProps;

  return function dispatchCustomSnack(content: JSX.Element): void {
    enqueueSnackbar(fakeNotistackMessage, {
      autoHideDuration,
      content,
      key: snackId,
      persist,
    });
  };
}

type LinkSnackProps = {
  linkText: string;
  message: string;
  url: string;
  'data-cy'?: string;
  externalLink?: boolean;
  variant?: VariantType;
};

type LinkSnackFn = (props: LinkSnackProps) => void;

export function useDispatchLinkSnack(props: DispatchCustomSnackProps = {}): LinkSnackFn {
  const { snackId = uuid4() } = props;
  const dispatchSnack = useDispatchCustomSnackFn({ ...props, snackId });

  return function dispatchLinkSnack(overrideProps: LinkSnackProps): void {
    dispatchSnack(<LinkSnackbar {...props} id={snackId} {...overrideProps} />);
  };
}

export function useLogAndDispatchErrorSnack(params?: MsgOptionalSnack): (error: Error) => void {
  const displayMessage = params?.message || defaultErrorMessage;

  const dispatchErrorNotification = useDispatchNotification({
    ...params,
    message: displayMessage,
    variant: 'error',
  });

  return function (error: Error): void {
    dispatchErrorNotification();
    console.error(error);
  };
}

export function useDispatchInfoSnack(params: MsgRequiredSnack): () => void {
  return useDispatchNotification({ ...params, variant: 'info' });
}

export function useDispatchUndoSnack(): (
  message: string,
  onClickUndo: (e: React.MouseEvent) => void,
  variant?: VariantType
) => void {
  const theme = useTheme();
  const { closeSnackbar, enqueueSnackbar } = useSnackbar();

  return function dispatchUndoSnack(message, onClickUndo, variant): void {
    // Generate key inside func so each instance of the snackbar gets a unique/diff key.
    const key = uuid4();

    enqueueSnackbar(fakeNotistackMessage, {
      content: (
        <BaseSnackbar
          action={
            <SelectButton
              style={{ color: theme.palette.primary.main }}
              onClick={e => {
                onClickUndo(e);
                closeSnackbar(key);
              }}
            >
              Undo
            </SelectButton>
          }
          id={key}
          message={message}
          variant={variant ?? 'success'}
        />
      ),
      key,
    });
  };
}

export function useDispatchButtonSnack<FuncArgs = unknown>(args: {
  buttonText: string;
  message: string;
  onClick: (e: React.MouseEvent, args?: FuncArgs) => void;
  autoHideDurationMS?: number;
  persist?: boolean;
  variant?: VariantType;
}): (args?: FuncArgs) => void {
  const theme = useTheme();
  const {
    autoHideDurationMS = defaultSnackBarDisplayTimeMS,
    buttonText,
    message,
    onClick,
    persist,
    variant,
  } = args;
  const { closeSnackbar, enqueueSnackbar } = useSnackbar();

  return function dispatchButtonSnack(args): void {
    // Generate key inside func so each instance of the snackbar gets a unique/diff key.
    const key = uuid4();

    enqueueSnackbar(fakeNotistackMessage, {
      autoHideDuration: autoHideDurationMS,
      content: (
        <BaseSnackbar
          action={
            <SelectButton
              style={{ color: theme.palette.primary.main }}
              onClick={e => {
                onClick(e, args);
                closeSnackbar(key);
              }}
            >
              {buttonText}
            </SelectButton>
          }
          id={key}
          message={message}
          variant={variant ?? 'success'}
        />
      ),
      key,
      persist,
    });
  };
}

export function useDispatchSuccessSnack(params?: MsgOptionalSnack): () => void {
  const displayMessage = params?.message || defaultSuccessMessage;
  return useDispatchNotification({ ...params, message: displayMessage, variant: 'success' });
}

interface DispatchNotification extends MsgRequiredSnack {
  variant: VariantType;
}

export function useDispatchNotification({
  autoHideDurationMS,
  key,
  message,
  variant,
}: DispatchNotification): () => void {
  return function (): void {
    pushNotification({
      autoHideDuration: autoHideDurationMS || defaultSnackBarDisplayTimeMS,
      key,
      message,
      variant,
    });
  };
}
